import { Box, Typography } from "@mui/material"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import { useNavigate } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useMemo, useState } from "react"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import ProductSearchSuggestion from "@/components/SearchSuggestion/ProductSearchSuggestion"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { type Product, ProductSortField, SortingOrder } from "@/graphql/codegen/graphql"
import useAssociateProductColumns from "@/screens/Products/hooks/useAssociateProductColumns"
import useSearchProductsForAssociation from "@/screens/Products/hooks/useSearchProductsForAssociation"
import type { DialogCloseAction } from "@/types/common"
import type { AutocompleteOption } from "@/types/form-fields"

interface AddAlternateSourceDialogProps {
  product: Product
  onClose: (action: DialogCloseAction) => void
  onSubmit: () => void
}

const PRODUCT_SELECTION_SORT_FIELD_MAP: Partial<Record<string, ProductSortField>> = {
  productId: ProductSortField.ProductId,
  companyProductDescription: ProductSortField.ProductSupplierDescription,
  vendorDescription: ProductSortField.ProductVendorDescription,
  vendor: ProductSortField.CompanyName,
  shipFrom: ProductSortField.ShipFromLocation,
}

export const AddAlternateSourceDialog: FC<AddAlternateSourceDialogProps> = ({ product, onClose, onSubmit }) => {
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [sorting, setSorting] = useState<SortingState>([])
  const [targetProduct, setTargetProduct] = useState<Product | null>(null)
  const navigate = useNavigate()

  const showSearchResults = searchQuery.length >= 2

  const handleSubmit = async () => {
    if (!targetProduct) return

    onSubmit()
    onClose("success")

    await navigate({
      to: "/app/product/$productId/source/$sourceId",
      params: { productId: product.productId, sourceId: targetProduct.productId },
    })
  }

  const productSort = useMemo(() => {
    if (!sorting[0]) return undefined
    const field = PRODUCT_SELECTION_SORT_FIELD_MAP[sorting[0].id]
    if (!field) return undefined
    return { field, order: sorting[0].desc ? SortingOrder.Desc : SortingOrder.Asc }
  }, [sorting])

  const { products, isFetchingProducts } = useSearchProductsForAssociation({
    query: searchQuery,
    showInactive: false,
    sort: productSort,
  })

  const onSearch = (searchedItem: string | AutocompleteOption) => {
    setSearchQuery(typeof searchedItem === "string" ? searchedItem : searchedItem.label)
  }

  const handleDialogClose = (_?: unknown, reason: DialogCloseAction = "cancel") => {
    onClose(reason)
  }

  const columns = useAssociateProductColumns({
    showRadioButton: true,
    onSelect: setTargetProduct,
    disabledProductIds: [product.productId],
  })

  const rowSelection = targetProduct ? { [targetProduct.productId.toString()]: true } : {}

  return (
    <Dialog open onClose={handleDialogClose} fullWidth maxWidth='lg' data-testid='product.alternate-source-dialog'>
      <DialogHeader title='Alternate Source' onClose={handleDialogClose} />

      <DialogContent classes={{ root: "p-10" }}>
        <Box className='flex flex-col gap-3'>
          <ProductSearchSuggestion onSearch={onSearch} />

          {showSearchResults ? (
            <Box>
              {!isFetchingProducts && products.length === 0 && (
                <Box className='flex flex-col items-center justify-center'>
                  <Typography className='text-gray-500'>No products found for "{searchQuery}"</Typography>
                </Box>
              )}

              {(isFetchingProducts || products.length > 0) && (
                <Box className='max-h-96 overflow-auto'>
                  <AerosBaseTable
                    features={{
                      tanstackOptions: {
                        data: products,
                        columns,
                        state: { isLoading: isFetchingProducts, rowSelection, sorting },
                        getRowId: (row) => row.productId.toString(),
                        onSortingChange: setSorting as OnChangeFn<SortingState>,
                        manualSorting: true,
                        enableMultiSort: false,
                        enableSortingRemoval: false,
                      },
                    }}
                    slotProps={{
                      bodyRow: {
                        className: "cursor-pointer hover:bg-primary-100",
                      },
                    }}
                    noDataMessage='No products found'
                  />
                </Box>
              )}
            </Box>
          ) : (
            <InformationMissingAlert title='Use the search field to find and select an alternate product.' />
          )}
        </Box>
      </DialogContent>

      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleDialogClose}
          data-testid='product.alternate-source-dialog.cancel-button'
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={!targetProduct}
          data-testid='product.alternate-source-dialog.continue-button'
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
